import { Controller } from "@hotwired/stimulus"
import { Turbo } from "@hotwired/turbo-rails"

import { lookCookie, PALETTE_COOKIE, THEME_COOKIE, themeCookieValue } from "../lib/site_look_cookie"
import { applyTheme, currentTheme, isTheme, rememberTheme, STORAGE_KEY, type Theme } from "../lib/theme_choice"

// The visitor's look switcher of a breeder Site on the switcher list
// (`public/shared/site_look_dialog`, full phase 097, `doc/site-layout.md`
// 5.4.3): the mode, the palette and the design theme the page is drawn in, in
// this browser only.
//
// The mode is the switch of phase 093 by the same functions
// (`lib/theme_choice.ts`): applied to `<html>` at once and remembered in
// `localStorage` when it differs from the Site's scheme. The palette and the
// theme are rendered by the server, so they are cookies it reads
// (`lib/site_look_cookie.ts`) and a reload: a choice equal to the Site's
// default deletes its cookie. A palette reloads through Turbo, which swaps
// the body where the palette block lives; a theme reloads the document,
// because a Turbo render copies only `lang` and `dir` onto `<html>`, and the
// theme is `<html data-site-theme>`. The reset ("as on the site") deletes
// both cookies and the mode.
export default class SiteLookController extends Controller<HTMLElement> {
  static targets = ["trigger", "mode"]
  static values = { defaultMode: { type: String, default: "system" }, defaultTheme: String }

  declare readonly triggerTarget: HTMLButtonElement
  declare readonly modeTargets: HTMLInputElement[]
  declare readonly defaultModeValue: string
  declare readonly defaultThemeValue: string

  // The head script has applied a remembered mode before first paint, so
  // the attribute as it stands is the mode to check.
  connect(): void {
    const mode = currentTheme(document.documentElement)
    for (const radio of this.modeTargets) radio.checked = radio.value === mode
    this.triggerTarget.hidden = false
  }

  choose(event: Event): void {
    const input = event.target
    if (!(input instanceof HTMLInputElement) || input.type !== "radio") return

    if (input.name === "site_look_mode") {
      if (isTheme(input.value)) this.applyMode(input.value)
    } else if (input.name === "site_look_palette") {
      writeCookie(PALETTE_COOKIE, input.value)
      Turbo.visit(window.location.href, { action: "replace" })
    } else if (input.name === "site_look_theme") {
      writeCookie(THEME_COOKIE, themeCookieValue(input.value, this.defaultThemeValue))
      window.location.reload()
    }
  }

  reset(): void {
    writeCookie(PALETTE_COOKIE, "")
    writeCookie(THEME_COOKIE, "")
    this.applyMode(this.siteMode())
    window.location.reload()
  }

  private applyMode(mode: Theme): void {
    applyTheme(document.documentElement, mode)
    rememberTheme(() => window.localStorage, STORAGE_KEY, mode, this.siteMode())
  }

  private siteMode(): Theme {
    return isTheme(this.defaultModeValue) ? this.defaultModeValue : "system"
  }
}

function writeCookie(name: string, value: string): void {
  document.cookie = lookCookie(name, value, window.location.protocol)
}
